/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed, reaction } from "mobx";
import {
  rhpDropinIds,
  UIControlToSkinnyRailVerbsMapping,
  rhpDropinIdsForCommenting,
  RHP_VERBS_POSITION,
  VERB_QANDA
} from "constants/enums";
import { updateComponentProperties } from "utils/APIUtils";
import { getShouldRenderCommentsDropin, getShouldRenderPersonalCommentsDropin } from "selectors/overlayDropinSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { overlayEventNames } from "constants/constants";
import { getShouldEnableEditGenAI, getShouldEnableOutline } from "selectors/appSelectors";
import { pageViewModifiers } from "../constants/enums";

class SkinnyRailStore {
  @observable componentProperties;
  @observable topUIControls;
  @observable bottomUIControls;
  @observable rhpOpenState;
  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init(switchPreview = false) {
    if (switchPreview) return;
    this.topUIControls = [];
    this.bottomUIControls = [];
    this.componentProperties = new Map();
    this.rhpOpenState = { visible: false };
    Object.values(UIControlToSkinnyRailVerbsMapping).forEach(verb =>
      this.componentProperties.set(verb, { visible: true })
    );
  }

  @action.bound
  setComponentProperties(propName, config) {
    // Close RHP if we remove the active dropin
    if (!config.visible) {
      const rhpRef = this.stores.appStore.rhpRefValue;
      if (rhpRef) {
        rhpRef.getPublicApis().then(apis => {
          const config = apis.getConfig();
          const activeDropin = config.dropinId;
          if (activeDropin === UIControlToSkinnyRailVerbsMapping[propName]) {
            apis.closePanel();
          }
        });
      }
    }

    // Update the component propeties to handle visibility of verbs in skinny rail
    updateComponentProperties(this.componentProperties, UIControlToSkinnyRailVerbsMapping[propName], config);
    if (propName === "COMMENTS_PANEL") {
      overlayEvents.trigger(overlayEventNames.COMMENTS_PANEL_VISIBILITY_CHANGED, config);
    }
  }

  /**
   * A computed funtion, returns the list of dropin id
   * which is going to be rendered in skinny rail.
   * This returned dropins is a filtered list from rhpDropinIds based on some factors like disabledVerbs.
   */
  @computed
  get enabledVerbs() {
    const dropinIds = Object.values(rhpDropinIds).filter(dropinId => {
      // Handling visibility of commenting related dropins in skinny rail
      if (rhpDropinIdsForCommenting.includes(dropinId)) {
        // Show comment tool if forceShowCommentingTools is enabled
        const forceShowCommentingTools = this.stores.documentStore.forceShowCommentingTools;
        if (forceShowCommentingTools) return true;

        // Show comment tool if commenting feature is enabled and UIConfig to show comment icon in skinny rail is enabled
        const shouldRenderCommentsDropin = getShouldRenderCommentsDropin(this.stores);
        const shouldRenderPersonalCommentsDropin = getShouldRenderPersonalCommentsDropin(this.stores);
        const isCommentingFeatureEnabled = shouldRenderCommentsDropin || shouldRenderPersonalCommentsDropin;
        if (isCommentingFeatureEnabled) {
          return !this.disabledVerbs.includes(dropinId);
        }
        return false;
      } else if (dropinId === rhpDropinIds.EDIT_GENAI_PANEL) {
        return getShouldEnableEditGenAI(this.stores);
      } else if (dropinId === VERB_QANDA) {
        return true;
      }

      // Handling visibility of other then commenting related dropins in skinny rail
      return !this.disabledVerbs.includes(dropinId);
    });
    return dropinIds;
  }

  /**
   * A computed function, returns the list of dropin ids,
   * which are disabled via UI Config
   */
  @computed
  get disabledVerbs() {
    const props = [];
    for (const [key, value] of this.componentProperties) {
      if (!value.visible) props.push(key);
    }
    return props;
  }

  @action.bound
  setCustomVerbsGroup({ controls, position = RHP_VERBS_POSITION.TOP }) {
    switch (position) {
      case RHP_VERBS_POSITION.TOP:
        this.topUIControls = [...new Set([...this.topUIControls, ...controls])];
        break;
      case RHP_VERBS_POSITION.BOTTOM:
        this.bottomUIControls = [...new Set([...this.bottomUIControls, ...controls])];
        break;
    }
  }

  @action.bound
  setRhpOpenState(config) {
    this.rhpOpenState = config;
  }

  @action setFitWidthModeForSharedFiles() {
    reaction(
      () => this.rhpOpenState,
      (rhpOpenState, reaction) => {
        if (rhpOpenState.visible) {
          this.shouldSetFitWidthModeForSharedFiles = true;
          reaction.dispose();
        }
      }
    );
  }

  @action
  setFitWidthModeIfNeeded() {
    if (this.shouldSetFitWidthModeForSharedFiles) {
      this.shouldSetFitWidthModeForSharedFiles = false;
      this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_WIDTH });
    }
  }
}

export { SkinnyRailStore };
